import React, { useState, useEffect } from "react";
import { Modal, Table, Button, message, Steps, Space, Tag, Input, InputNumber, Select, Dropdown, Popover, Typography } from "antd";
import { CheckCircleOutlined, EditOutlined, SwapOutlined, CloseOutlined, EyeOutlined, CopyOutlined, PlusOutlined } from "@ant-design/icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { baseService } from "~/services/base";
import { useGetClients } from "~/services/client/client.queries";
import { ClientStatus, InventoryStatusCode, PackingTypes } from "~/utils/constants";
import { INVENTORY_KEYS } from "~/services/inventory/keys";
import { InventoryType } from "~/utils/constants";

const { TextArea } = Input;

type ParsedInventoryRow = {
  title?: string;
  artistName?: string;
  medium?: string;
  description?: string;
  pieceCount?: string;
  packageCount?: string;
  length?: number;
  width?: number;
  height?: number;
  weight?: number;
  value?: number;
  clientInternalRef?: string;
  notes?: string;
  storageRef?: string;
  locationRef?: string;
  itemType?: InventoryType;
  status?: InventoryStatusCode;
  packingType?: PackingTypes;
  _originalRowData?: Record<string, string>; // Store original CSV row data
  _originalRowIndex?: number; // Store original row index
  [key: string]: any;
};

type CsvImportPreviewModalProps = {
  open: boolean;
  file: File | null;
  clientId?: string;
  onClose: () => void;
  onSuccess?: () => void;
  inline?: boolean;
};

export const CsvImportPreviewModal: React.FC<CsvImportPreviewModalProps> = ({
  open,
  file,
  clientId: initialClientId,
  onClose,
  onSuccess,
  inline = false,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedClientId, setSelectedClientId] = useState<string | undefined>(initialClientId);
  const [parsedData, setParsedData] = useState<ParsedInventoryRow[]>([]);
  const [editedData, setEditedData] = useState<ParsedInventoryRow[]>([]);
  const [originalHeaders, setOriginalHeaders] = useState<string[]>([]);
  const [lastDeletedRow, setLastDeletedRow] = useState<{ row: ParsedInventoryRow; index: number } | null>(null);
  const [bulkItemType, setBulkItemType] = useState<InventoryType | undefined>(undefined);
  const [bulkStatus, setBulkStatus] = useState<InventoryStatusCode | undefined>(undefined);
  const [bulkPackingType, setBulkPackingType] = useState<PackingTypes | undefined>(undefined);

  const queryClient = useQueryClient();
  const { data: clientsResponse, isLoading: clientsLoading } = useGetClients({
    params: { status: ClientStatus.ACTIVE, sortByName: "ASC", getAll: true },
  });
  const clients = clientsResponse?.items || [];

  // Parse CSV when file is provided
  useEffect(() => {
    if (file && open) {
      parseCsvFile(file);
    }
  }, [file, open]);

  // Simple CSV parser that handles quoted fields
  const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = "";
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];
      
      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          // Escaped quote
          current += '"';
          i++; // Skip next quote
        } else {
          // Toggle quote state
          inQuotes = !inQuotes;
        }
      } else if (char === "," && !inQuotes) {
        // End of field
        result.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }
    
    // Add last field
    result.push(current.trim());
    return result;
  };

  // Round up to the nearest 0.25
  const roundUpToQuarter = (value: number): number => {
    return Math.ceil(value * 4) / 4;
  };

  // Parse dimensions string to extract length, width, height
  // Handles formats like:
  // - "26 x 25 1/2 inches (66 x 64.8 cm)"
  // - "60 x 48 inches (152.4 x 121.9 cm)"
  // - "paper size: 24 x 60 inches (61 x 152.4 cm)\nframe size: 28 1/8 x 64 1/8 inches"
  // If 2 dimensions: length x height (no width)
  // If 3 dimensions: length x width x height
  // All dimensions are rounded up to the nearest 0.25
  const parseDimensions = (dimensionsStr: string): { length?: number; width?: number; height?: number } => {
    if (!dimensionsStr) return {};
    
    // Helper to parse fractions like "25 1/2" = 25.5 or "1/2" = 0.5
    const parseFraction = (str: string): number => {
      const trimmed = str.trim();
      // Match "whole number + fraction" like "25 1/2"
      const wholeFractionMatch = trimmed.match(/(\d+)\s+(\d+)\/(\d+)/);
      if (wholeFractionMatch) {
        const whole = parseFloat(wholeFractionMatch[1]);
        const num = parseFloat(wholeFractionMatch[2]);
        const den = parseFloat(wholeFractionMatch[3]);
        return whole + (num / den);
      }
      // Match just fraction like "1/2"
      const fractionMatch = trimmed.match(/(\d+)\/(\d+)/);
      if (fractionMatch) {
        const num = parseFloat(fractionMatch[1]);
        const den = parseFloat(fractionMatch[2]);
        return num / den;
      }
      // Just a number
      return parseFloat(trimmed) || 0;
    };
    
    // Prefer frame size if available, otherwise use artwork/image/paper size
    let workingStr = dimensionsStr;
    const frameMatch = dimensionsStr.match(/frame\s+size[:\s]+([^(\n]+)/i);
    if (frameMatch) {
      workingStr = frameMatch[1];
    } else {
      // Try to find artwork/image/paper size
      const artworkMatch = dimensionsStr.match(/(?:artwork|image|paper)\s+size[:\s]+([^(\n]+)/i);
      if (artworkMatch) {
        workingStr = artworkMatch[1];
      }
    }
    
    // Try to find metric dimensions first (more reliable, in parentheses)
    const metricMatch = dimensionsStr.match(/\(([\d.\s\/]+)\s*x\s*([\d.\s\/]+)(?:\s*x\s*([\d.\s\/]+))?\s*cm\)/i);
    if (metricMatch) {
      const length = parseFraction(metricMatch[1].trim());
      const second = parseFraction(metricMatch[2].trim());
      const third = metricMatch[3] ? parseFraction(metricMatch[3].trim()) : undefined;
      
      // Convert cm to inches (1 inch = 2.54 cm) and round up to nearest 0.25
      if (third !== undefined) {
        // 3 dimensions: length x width x height
        return {
          length: roundUpToQuarter(length / 2.54),
          width: roundUpToQuarter(second / 2.54),
          height: roundUpToQuarter(third / 2.54),
        };
      } else {
        // 2 dimensions: length x height (no width)
        return {
          length: roundUpToQuarter(length / 2.54),
          height: roundUpToQuarter(second / 2.54),
        };
      }
    }
    
    // Fallback to imperial dimensions from working string
    // Match patterns like "26 x 25 1/2" or "60 x 48" or "26 x 25 1/2 x 3"
    const imperialMatch = workingStr.match(/([\d\s\/]+)\s*x\s*([\d\s\/]+)(?:\s*x\s*([\d\s\/]+))?/i);
    if (imperialMatch) {
      const length = parseFraction(imperialMatch[1].trim());
      const second = parseFraction(imperialMatch[2].trim());
      const third = imperialMatch[3] ? parseFraction(imperialMatch[3].trim()) : undefined;
      
      if (third !== undefined) {
        // 3 dimensions: length x width x height
        return {
          length: roundUpToQuarter(length),
          width: roundUpToQuarter(second),
          height: roundUpToQuarter(third),
        };
      } else {
        // 2 dimensions: length x height (no width)
        return {
          length: roundUpToQuarter(length),
          height: roundUpToQuarter(second),
        };
      }
    }
    
    return {};
  };

  // Combine signed and edition info into notes
  const combineNotes = (signed?: string, edition?: string): string => {
    const parts: string[] = [];
    if (signed && signed.trim()) {
      parts.push(`Signed: ${signed.trim()}`);
    }
    if (edition && edition.trim()) {
      parts.push(`Edition: ${edition.trim()}`);
    }
    return parts.join(". ");
  };

  const parseCsvFile = async (csvFile: File) => {
    try {
      const text = await csvFile.text();
      const lines = text.split("\n").filter((line) => line.trim());
      
      if (lines.length === 0) {
        message.error("CSV file is empty");
        return;
      }

      // Parse header row
      const headers = parseCSVLine(lines[0]).map((h) => h.trim().replace(/^"|"$/g, ""));
      setOriginalHeaders(headers);
      
      // Parse data rows
      const parsed: ParsedInventoryRow[] = [];
      for (let i = 1; i < lines.length; i++) {
        const values = parseCSVLine(lines[i]).map((v) => v.trim().replace(/^"|"$/g, ""));
        const row: ParsedInventoryRow = {};
        
        // Store original row data
        const originalRowData: Record<string, string> = {};
        headers.forEach((header, idx) => {
          originalRowData[header] = values[idx] || "";
        });
        row._originalRowData = originalRowData;
        row._originalRowIndex = i; // 1-based index (line 1 is header, so first data row is index 1)
        
        // Track values for special processing
        let title = "";
        let dateOfArtwork = "";
        let signed = "";
        let edition = "";
        let dimensions = "";
        
        headers.forEach((header, index) => {
          const value = values[index] || "";
          const headerUpper = header.toUpperCase().trim();
          
          // Map CSV headers to our fields
          if (headerUpper === "INVENTORYNUM" || headerUpper === "INVENTORY_NUM" || headerUpper === "INVENTORY #") {
            // InventoryNum maps to clientInternalRef (how they track inventory)
            row.clientInternalRef = value;
          } else if (headerUpper === "ARTIST") {
            row.artistName = value;
          } else if (headerUpper === "TITLE" || (headerUpper.includes("TITLE") && !headerUpper.includes("DATE"))) {
            title = value;
          } else if (headerUpper === "DATE" || headerUpper === "DATEOFARTWORK" || headerUpper === "DATE_OF_ARTWORK") {
            dateOfArtwork = value;
          } else if (headerUpper === "MEDIUM") {
            row.medium = value;
          } else if (headerUpper === "DIMENSIONS") {
            dimensions = value;
            // Parse dimensions
            const dims = parseDimensions(value);
            row.length = dims.length;
            row.width = dims.width;
            row.height = dims.height;
          } else if (headerUpper === "SIGNED") {
            signed = value;
          } else if (headerUpper === "EDITION") {
            edition = value;
          } else if (headerUpper.includes("CLIENT_INTERNAL_REF") || headerUpper.includes("CLIENT INTERNAL REF") || headerUpper.includes("INTERNAL REF") || headerUpper === "INTERNAL_REF") {
            row.clientInternalRef = value;
          } else if (headerUpper === "LENGTH" || headerUpper.includes("LENGTH")) {
            row.length = value ? roundUpToQuarter(parseFloat(value)) : undefined;
          } else if (headerUpper === "WIDTH" || headerUpper.includes("WIDTH")) {
            row.width = value ? roundUpToQuarter(parseFloat(value)) : undefined;
          } else if (headerUpper === "HEIGHT" || headerUpper.includes("HEIGHT")) {
            row.height = value ? roundUpToQuarter(parseFloat(value)) : undefined;
          } else if (headerUpper === "WEIGHT" || headerUpper.includes("WEIGHT")) {
            row.weight = value ? parseFloat(value) : undefined;
          } else if (headerUpper === "VALUE" || headerUpper.includes("VALUE") || headerUpper.includes("PRICE")) {
            row.value = value ? parseFloat(value.replace(/[^0-9.]/g, "")) : undefined;
          } else if ((headerUpper.includes("PIECE") && headerUpper.includes("COUNT")) || headerUpper === "PIECE_COUNT") {
            row.pieceCount = value;
          } else if ((headerUpper.includes("PACKAGE") && headerUpper.includes("COUNT")) || headerUpper === "PACKAGE_COUNT") {
            row.packageCount = value;
          } else if (headerUpper.includes("DESCRIPTION")) {
            row.description = value;
          } else if (headerUpper.includes("NOTE") && !headerUpper.includes("INTERNAL")) {
            row.notes = value;
          } else if (headerUpper === "STORAGE_REF" || headerUpper.includes("STORAGE REF")) {
            row.storageRef = value;
          } else if (headerUpper === "LOCATION_REF" || headerUpper.includes("LOCATION REF")) {
            row.locationRef = value;
          } else if (headerUpper === "ITEM_TYPE" || headerUpper.includes("ITEM TYPE") || headerUpper === "TYPE") {
            row.itemType = value as InventoryType;
          } else if (headerUpper === "STATUS") {
            // Try to match status value
            const statusUpper = value.toUpperCase();
            if (statusUpper === "NO_POSSESSION" || statusUpper.includes("NO POSSESSION")) {
              row.status = InventoryStatusCode.NO_POSSESSION;
            } else if (statusUpper === "AVAILABLE_ON_HAND" || statusUpper.includes("ON HAND")) {
              row.status = InventoryStatusCode.AVAILABLE_ON_HAND;
            } else if (statusUpper === "AVAILABLE_LIVE" || statusUpper.includes("LIVE")) {
              row.status = InventoryStatusCode.AVAILABLE_LIVE;
            } else if (statusUpper === "RELEASED") {
              row.status = InventoryStatusCode.RELEASED;
            }
          } else if (headerUpper === "PACKING_TYPE" || headerUpper.includes("PACKING TYPE") || headerUpper === "PACKAGING") {
            // Try to match packing type value
            const packingUpper = value.toUpperCase().replace(/[_\s]/g, "_");
            // Match common variations
            const packingTypeMap: Record<string, PackingTypes> = {
              "LOOSE": PackingTypes.LOOSE,
              "SLIPCASE": PackingTypes.SLIPCASE,
              "CB_FOLD": PackingTypes.CB_FOLD,
              "CBFOLD": PackingTypes.CB_FOLD,
              "SHADOW_BOX": PackingTypes.SHADOW_BOX,
              "SHADOWBOX": PackingTypes.SHADOW_BOX,
              "FLOATING_SHADOW_BOX": PackingTypes.FLOATING_SHADOW_BOX,
              "FLOATINGSHADOWBOX": PackingTypes.FLOATING_SHADOW_BOX,
              "POLY_ONLY": PackingTypes.POLY_ONLY,
              "POLYONLY": PackingTypes.POLY_ONLY,
              "TRAVEL_FRAME": PackingTypes.TRAVEL_FRAME,
              "TRAVELFRAME": PackingTypes.TRAVEL_FRAME,
              "CRATE": PackingTypes.CRATE,
              "SLATCRATE": PackingTypes.SLATCRATE,
              "SLAT_CRATE": PackingTypes.SLATCRATE,
              "PALLET": PackingTypes.PALLET,
              "COMMERCIAL_BIN": PackingTypes.COMMERCIAL_BIN,
              "COMMERCIALBIN": PackingTypes.COMMERCIAL_BIN,
              "CBIN": PackingTypes.CBIN,
              "C_BIN": PackingTypes.CBIN,
              "UNKNOWN_PACKAGING": PackingTypes.UNKNOWN_PACKAGING,
              "UNKNOWNPACKAGING": PackingTypes.UNKNOWN_PACKAGING,
              "BOX": PackingTypes.BOX,
              "CUSTOM": PackingTypes.CUSTOM,
              "T_FRAME": PackingTypes.T_Frame,
              "TFRAME": PackingTypes.T_Frame,
              "BLANKET_WRAPPED": PackingTypes.BLANKET_WRAPPED,
              "BLANKETWRAPPED": PackingTypes.BLANKET_WRAPPED,
              "POLY_FOAM": PackingTypes.POLY_FOAM,
              "POLYFOAM": PackingTypes.POLY_FOAM,
              "POLY_FOAM_CB": PackingTypes.POLY_FOAM_CB,
              "POLYFOAMCB": PackingTypes.POLY_FOAM_CB,
            };
            if (packingTypeMap[packingUpper]) {
              row.packingType = packingTypeMap[packingUpper];
            } else if (Object.values(PackingTypes).includes(value as PackingTypes)) {
              row.packingType = value as PackingTypes;
            }
          } else {
            // Store unknown headers for reference
            row[header] = value;
          }
        });
        
        // Process title: append date if present
        if (title) {
          row.title = dateOfArtwork ? `${title}, ${dateOfArtwork}` : title;
        }
        
        // Combine signed and edition into notes
        const notesFromFields = combineNotes(signed, edition);
        if (notesFromFields) {
          row.notes = row.notes ? `${row.notes}. ${notesFromFields}` : notesFromFields;
        }
        
        // Set default status if not provided
        if (!row.status) {
          row.status = InventoryStatusCode.NO_POSSESSION;
        }
        
        // Set default packing type if not provided
        if (!row.packingType) {
          row.packingType = PackingTypes.LOOSE;
        }
        
        // Set default width to 4" if not available but length or height exists
        if (row.width === undefined && (row.length !== undefined || row.height !== undefined)) {
          row.width = 4;
        }
        
        // Set default weight to 10 if not provided
        if (row.weight === undefined || row.weight === null) {
          row.weight = 10;
        }
        
        // Set default value to 0 if not provided
        if (row.value === undefined || row.value === null) {
          row.value = 0;
        }
        
        // Only add non-empty rows
        if (Object.values(row).some((v) => v !== "" && v !== undefined && v !== null)) {
          parsed.push(row);
        }
      }

      setParsedData(parsed);
      setEditedData(parsed);
      setCurrentStep(1);
      message.success(`Parsed ${parsed.length} inventory items from CSV`);
    } catch (error: any) {
      console.error("CSV parsing error:", error);
      message.error(error.message || "Failed to parse CSV file");
    }
  };

  const importMutation = useMutation({
    mutationFn: async (data: { clientId: string; inventories: ParsedInventoryRow[] }) => {
      // Convert to CSV format matching backend expectations
      const formData = new FormData();
      
      // Get client to find clientRef
      const client = clients.find((c) => c.id === data.clientId);
      if (!client) {
        throw new Error("Client not found");
      }
      const clientRef = client.clientRef || "";
      
      // Create CSV with headers that backend expects
      const headers = [
        "TITLE",
        "ARTIST_NAME",
        "MEDIUM",
        "CLIENT_REF",
        "CLIENT_INTERNAL_REF",
        "LENGTH",
        "WIDTH",
        "HEIGHT",
        "WEIGHT",
        "VALUE",
        "PIECE_COUNT",
        "PACKAGE_COUNT",
        "DESCRIPTION",
        "NOTES",
        "STORAGE_REF",
        "LOCATION_REF",
        "ITEM_TYPE",
        "STATUS",
        "PACKING_TYPE",
      ];
      
      // Remove CLIENT_REF from headers if we're not using it
      // (We use the selected client dropdown instead)
      
      // Helper to escape CSV values
      const escapeCsvValue = (value: any): string => {
        if (value === null || value === undefined || value === "") return "";
        const str = String(value);
        // If contains comma, quote, or newline, wrap in quotes and escape quotes
        if (str.includes(",") || str.includes('"') || str.includes("\n")) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };
      
      const csvRows = [
        headers.join(","),
        ...data.inventories.map((inv) =>
          headers.map((h) => {
            const fieldMap: Record<string, any> = {
              TITLE: inv.title || "",
              ARTIST_NAME: inv.artistName || "",
              MEDIUM: inv.medium || "",
              CLIENT_REF: clientRef || "",
              CLIENT_INTERNAL_REF: inv.clientInternalRef || "",
              LENGTH: inv.length || "",
              WIDTH: inv.width !== undefined ? inv.width : (inv.length !== undefined || inv.height !== undefined ? 4 : ""),
              HEIGHT: inv.height || "",
              WEIGHT: inv.weight !== undefined ? inv.weight : 10,
              VALUE: inv.value !== undefined ? inv.value : 0,
              PIECE_COUNT: inv.pieceCount || "",
              PACKAGE_COUNT: inv.packageCount || "",
              DESCRIPTION: inv.description || "",
              NOTES: inv.notes || "",
              STORAGE_REF: inv.storageRef || "",
              LOCATION_REF: inv.locationRef || "",
              ITEM_TYPE: inv.itemType || InventoryType.PIECE,
              STATUS: inv.status || InventoryStatusCode.NO_POSSESSION,
              PACKING_TYPE: inv.packingType || PackingTypes.LOOSE,
            };
            return escapeCsvValue(fieldMap[h]);
          }).join(",")
        ),
      ];
      
      const csvContent = csvRows.join("\n");
      const csvBlob = new Blob([csvContent], { type: "text/csv" });
      const csvFile = new File([csvBlob], "import.csv", { type: "text/csv" });
      
      formData.append("file", csvFile);
      
      const response = await baseService.post("/inventories/import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    },
    onSuccess: (data) => {
      message.success(`Successfully imported ${data.created || 0} out of ${data.total || 0} inventory items`);
      queryClient.invalidateQueries({ queryKey: INVENTORY_KEYS.GET_INVENTORIES() });
      handleClose();
      onSuccess?.();
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || "Failed to import inventory");
    },
  });

  const handleFieldChange = (index: number, field: string, value: any) => {
    const updatedData = [...editedData];
    updatedData[index] = { ...updatedData[index], [field]: value };
    setEditedData(updatedData);
  };

  const handleSwapDimensions = (fromField: "length" | "width" | "height", toField: "length" | "width" | "height") => {
    const updatedData = editedData.map((item) => {
      const temp = item[fromField];
      return {
        ...item,
        [fromField]: item[toField],
        [toField]: temp,
      };
    });
    setEditedData(updatedData);
    message.success(`Swapped ${fromField} with ${toField}`);
  };

  const handleRemoveRow = (index: number) => {
    const rowToDelete = editedData[index];
    const updatedData = editedData.filter((_, i) => i !== index);
    setEditedData(updatedData);
    
    // Store the deleted row for undo
    setLastDeletedRow({ row: rowToDelete, index });
    
    // Show message with undo action
    const hide = message.success({
      content: (
        <span>
          Row removed.{" "}
          <a
            onClick={() => {
              handleUndoDelete();
              hide();
            }}
            style={{ textDecoration: "underline", cursor: "pointer" }}
          >
            Undo
          </a>
        </span>
      ),
      duration: 5, // Auto-hide after 5 seconds
    });
  };

  const handleUndoDelete = () => {
    if (lastDeletedRow) {
      const { row, index } = lastDeletedRow;
      const updatedData = [...editedData];
      updatedData.splice(index, 0, row);
      setEditedData(updatedData);
      setLastDeletedRow(null);
      message.success("Row restored");
    }
  };

  const handleDuplicateRow = (index: number) => {
    const rowToDuplicate = editedData[index];
    const duplicated: ParsedInventoryRow = {
      ...rowToDuplicate,
      _originalRowData: undefined,
      _originalRowIndex: undefined,
    };
    const updatedData = [...editedData];
    updatedData.splice(index + 1, 0, duplicated);
    setEditedData(updatedData);
    message.success("Row duplicated");
  };

  const handleAddRow = () => {
    const newRow: ParsedInventoryRow = {
      title: "",
      artistName: "",
      medium: "",
      itemType: InventoryType.PIECE,
      status: InventoryStatusCode.NO_POSSESSION,
      packingType: PackingTypes.LOOSE,
      weight: 10,
      value: 0,
    };
    setEditedData([...editedData, newRow]);
    message.success("New row added");
  };

  const handleBulkUpdate = () => {
    if (!bulkItemType && !bulkStatus && !bulkPackingType) {
      message.warning("Please select at least one field to update");
      return;
    }

    const updatedData = editedData.map((row) => ({
      ...row,
      itemType: bulkItemType !== undefined ? bulkItemType : row.itemType,
      status: bulkStatus !== undefined ? bulkStatus : row.status,
      packingType: bulkPackingType !== undefined ? bulkPackingType : row.packingType,
    }));

    setEditedData(updatedData);
    
    const fieldsUpdated: string[] = [];
    if (bulkItemType !== undefined) fieldsUpdated.push("Item Type");
    if (bulkStatus !== undefined) fieldsUpdated.push("Status");
    if (bulkPackingType !== undefined) fieldsUpdated.push("Packing Type");
    
    message.success(`Updated ${fieldsUpdated.join(", ")} for all ${updatedData.length} rows`);
    
    // Clear bulk values after applying
    setBulkItemType(undefined);
    setBulkStatus(undefined);
    setBulkPackingType(undefined);
  };

  const renderOriginalRowData = (record: ParsedInventoryRow) => {
    if (!record._originalRowData) return "No original data available";
    
    return (
      <div className="space-y-1 max-w-md">
        <Typography.Text strong className="text-xs">Original CSV Row {record._originalRowIndex}:</Typography.Text>
        <div className="bg-gray-50 p-2 rounded border text-xs font-mono">
          {originalHeaders.map((header) => (
            <div key={header} className="mb-1">
              <Typography.Text strong className="text-gray-600">{header}:</Typography.Text>{" "}
              <Typography.Text>{record._originalRowData[header] || "(empty)"}</Typography.Text>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const handleImport = () => {
    if (!selectedClientId) {
      message.error("Please select a client");
      return;
    }

    if (editedData.length === 0) {
      message.error("No data to import");
      return;
    }

    importMutation.mutate({
      clientId: selectedClientId,
      inventories: editedData,
    });
  };

  const handleClose = () => {
    setCurrentStep(0);
    setSelectedClientId(initialClientId);
    setParsedData([]);
    setEditedData([]);
    setLastDeletedRow(null);
    onClose();
  };

  const columns = [
    {
      title: "",
      key: "actions",
      width: 110,
      fixed: "left" as const,
      render: (_: any, record: ParsedInventoryRow, index: number) => (
        <Space>
          {record._originalRowData && (
            <Popover
              content={renderOriginalRowData(record)}
              title="Original CSV Data"
              trigger="click"
            >
              <Button
                type="text"
                size="small"
                icon={<EyeOutlined />}
                title="View original CSV row"
              />
            </Popover>
          )}
          <Button
            type="text"
            size="small"
            icon={<CopyOutlined />}
            onClick={() => handleDuplicateRow(index)}
            title="Duplicate row"
          />
          <Button
            type="text"
            size="small"
            danger
            icon={<CloseOutlined />}
            onClick={() => handleRemoveRow(index)}
            title="Remove row"
          />
        </Space>
      ),
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: 200,
      render: (text: string, record: ParsedInventoryRow, index: number) => (
        <Input
          value={text || ""}
          onChange={(e) => handleFieldChange(index, "title", e.target.value)}
          placeholder="Title"
        />
      ),
    },
    {
      title: "Artist",
      dataIndex: "artistName",
      key: "artistName",
      width: 150,
      render: (text: string, record: ParsedInventoryRow, index: number) => (
        <Input
          value={text || ""}
          onChange={(e) => handleFieldChange(index, "artistName", e.target.value)}
          placeholder="Artist"
        />
      ),
    },
    {
      title: "Medium",
      dataIndex: "medium",
      key: "medium",
      width: 150,
      render: (text: string, record: ParsedInventoryRow, index: number) => (
        <Input
          value={text || ""}
          onChange={(e) => handleFieldChange(index, "medium", e.target.value)}
          placeholder="Medium"
        />
      ),
    },
    {
      title: "Client Internal Ref",
      dataIndex: "clientInternalRef",
      key: "clientInternalRef",
      width: 150,
      render: (text: string, record: ParsedInventoryRow, index: number) => (
        <Input
          value={text || ""}
          onChange={(e) => handleFieldChange(index, "clientInternalRef", e.target.value)}
          placeholder="Inventory #"
        />
      ),
    },
    {
      title: (
        <div className="flex items-center justify-between">
          <span>Length</span>
          <Dropdown
            menu={{
              items: [
                { key: "width", label: "Swap with Width", onClick: () => handleSwapDimensions("length", "width") },
                { key: "height", label: "Swap with Height", onClick: () => handleSwapDimensions("length", "height") },
              ],
            }}
            trigger={["click"]}
          >
            <Button type="text" size="small" icon={<SwapOutlined />} />
          </Dropdown>
        </div>
      ),
      dataIndex: "length",
      key: "length",
      width: 120,
      render: (value: number, record: ParsedInventoryRow, index: number) => (
        <InputNumber
          value={value}
          onChange={(val) => {
            const rounded = val !== null && val !== undefined ? roundUpToQuarter(val) : val;
            handleFieldChange(index, "length", rounded);
          }}
          placeholder="in"
          style={{ width: "100%" }}
          min={0}
          step={0.25}
        />
      ),
    },
    {
      title: (
        <div className="flex items-center justify-between">
          <span>Width</span>
          <Dropdown
            menu={{
              items: [
                { key: "length", label: "Swap with Length", onClick: () => handleSwapDimensions("width", "length") },
                { key: "height", label: "Swap with Height", onClick: () => handleSwapDimensions("width", "height") },
              ],
            }}
            trigger={["click"]}
          >
            <Button type="text" size="small" icon={<SwapOutlined />} />
          </Dropdown>
        </div>
      ),
      dataIndex: "width",
      key: "width",
      width: 120,
      render: (value: number, record: ParsedInventoryRow, index: number) => (
        <InputNumber
          value={value}
          onChange={(val) => {
            // If width is being cleared and we have length/height, set to 4
            if (val === null || val === undefined) {
              if (record.length !== undefined || record.height !== undefined) {
                handleFieldChange(index, "width", 4);
                return;
              }
            }
            const rounded = val !== null && val !== undefined ? roundUpToQuarter(val) : val;
            handleFieldChange(index, "width", rounded);
          }}
          placeholder="in"
          style={{ width: "100%" }}
          min={0}
          step={0.25}
        />
      ),
    },
    {
      title: (
        <div className="flex items-center justify-between">
          <span>Height</span>
          <Dropdown
            menu={{
              items: [
                { key: "length", label: "Swap with Length", onClick: () => handleSwapDimensions("height", "length") },
                { key: "width", label: "Swap with Width", onClick: () => handleSwapDimensions("height", "width") },
              ],
            }}
            trigger={["click"]}
          >
            <Button type="text" size="small" icon={<SwapOutlined />} />
          </Dropdown>
        </div>
      ),
      dataIndex: "height",
      key: "height",
      width: 120,
      render: (value: number, record: ParsedInventoryRow, index: number) => (
        <InputNumber
          value={value}
          onChange={(val) => {
            const rounded = val !== null && val !== undefined ? roundUpToQuarter(val) : val;
            handleFieldChange(index, "height", rounded);
          }}
          placeholder="in"
          style={{ width: "100%" }}
          min={0}
          step={0.25}
        />
      ),
    },
    {
      title: "Weight",
      dataIndex: "weight",
      key: "weight",
      width: 100,
      render: (value: number, record: ParsedInventoryRow, index: number) => (
        <InputNumber
          value={value}
          onChange={(val) => handleFieldChange(index, "weight", val || 10)}
          placeholder="lbs"
          style={{ width: "100%" }}
          min={10}
        />
      ),
    },
    {
      title: "Value",
      dataIndex: "value",
      key: "value",
      width: 100,
      render: (value: number, record: ParsedInventoryRow, index: number) => (
        <InputNumber
          value={value}
          onChange={(val) => handleFieldChange(index, "value", val || 0)}
          placeholder="$"
          style={{ width: "100%" }}
          min={0}
          prefix="$"
        />
      ),
    },
    {
      title: "Item Type",
      dataIndex: "itemType",
      key: "itemType",
      width: 120,
      render: (value: InventoryType, record: ParsedInventoryRow, index: number) => (
        <Select
          value={value}
          onChange={(val) => handleFieldChange(index, "itemType", val)}
          style={{ width: "100%" }}
          options={[
            { label: "Piece", value: InventoryType.PIECE },
            { label: "Package", value: InventoryType.PACKAGE },
          ]}
        />
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 150,
      render: (value: InventoryStatusCode, record: ParsedInventoryRow, index: number) => (
        <Select
          value={value || InventoryStatusCode.NO_POSSESSION}
          onChange={(val) => handleFieldChange(index, "status", val)}
          style={{ width: "100%" }}
          options={[
            { label: "No Possession", value: InventoryStatusCode.NO_POSSESSION },
            { label: "Available On Hand", value: InventoryStatusCode.AVAILABLE_ON_HAND },
            { label: "Available Live", value: InventoryStatusCode.AVAILABLE_LIVE },
            { label: "Released", value: InventoryStatusCode.RELEASED },
          ]}
        />
      ),
    },
    {
      title: "Packing Type",
      dataIndex: "packingType",
      key: "packingType",
      width: 180,
      render: (value: PackingTypes, record: ParsedInventoryRow, index: number) => (
        <Select
          value={value || PackingTypes.LOOSE}
          onChange={(val) => handleFieldChange(index, "packingType", val)}
          style={{ width: "100%" }}
          showSearch
          filterOption={(input, option) =>
            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
          options={[
            { label: "Loose", value: PackingTypes.LOOSE },
            { label: "Slipcase", value: PackingTypes.SLIPCASE },
            { label: "CB Fold", value: PackingTypes.CB_FOLD },
            { label: "Shadow Box", value: PackingTypes.SHADOW_BOX },
            { label: "Floating Shadow Box", value: PackingTypes.FLOATING_SHADOW_BOX },
            { label: "Poly Only", value: PackingTypes.POLY_ONLY },
            { label: "Travel Frame", value: PackingTypes.TRAVEL_FRAME },
            { label: "Crate", value: PackingTypes.CRATE },
            { label: "Slat Crate", value: PackingTypes.SLATCRATE },
            { label: "Pallet", value: PackingTypes.PALLET },
            { label: "Commercial Bin", value: PackingTypes.COMMERCIAL_BIN },
            { label: "C-Bin", value: PackingTypes.CBIN },
            { label: "Unknown Packaging", value: PackingTypes.UNKNOWN_PACKAGING },
            { label: "Box", value: PackingTypes.BOX },
            { label: "Custom", value: PackingTypes.CUSTOM },
            { label: "T-Frame", value: PackingTypes.T_Frame },
            { label: "Blanket Wrapped", value: PackingTypes.BLANKET_WRAPPED },
            { label: "Poly/Foam", value: PackingTypes.POLY_FOAM },
            { label: "Poly/Foam/CB", value: PackingTypes.POLY_FOAM_CB },
          ]}
        />
      ),
    },
    {
      title: "Notes",
      dataIndex: "notes",
      key: "notes",
      width: 200,
      render: (text: string, record: ParsedInventoryRow, index: number) => (
        <TextArea
          value={text || ""}
          onChange={(e) => handleFieldChange(index, "notes", e.target.value)}
          placeholder="Notes (signed, edition, etc.)"
          rows={2}
          autoSize={{ minRows: 1, maxRows: 3 }}
        />
      ),
    },
  ];

  const content = (
    <>
      <Steps current={currentStep} className="mb-6">
        <Steps.Step title="Parse CSV" />
        <Steps.Step title="Review & Edit" />
        <Steps.Step title="Import" />
      </Steps>

      {currentStep === 0 && (
        <div className="text-center py-8">
          <p>Parsing CSV file...</p>
        </div>
      )}

      {currentStep === 1 && (
        <div className="space-y-4">
          {/* Bulk Update Section */}
          <div className="bg-gray-50 border-2 border-gray-300 rounded p-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="font-semibold text-sm">Bulk Update:</span>
              <span className="text-xs text-gray-600">Apply to all {editedData.length} rows</span>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-600">Item Type:</span>
                <Select
                  value={bulkItemType}
                  onChange={setBulkItemType}
                  placeholder="Select to update all"
                  style={{ width: 120 }}
                  allowClear
                  options={[
                    { label: "Piece", value: InventoryType.PIECE },
                    { label: "Package", value: InventoryType.PACKAGE },
                  ]}
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-600">Status:</span>
                <Select
                  value={bulkStatus}
                  onChange={setBulkStatus}
                  placeholder="Select to update all"
                  style={{ width: 160 }}
                  allowClear
                  options={[
                    { label: "No Possession", value: InventoryStatusCode.NO_POSSESSION },
                    { label: "Available On Hand", value: InventoryStatusCode.AVAILABLE_ON_HAND },
                    { label: "Available Live", value: InventoryStatusCode.AVAILABLE_LIVE },
                    { label: "Released", value: InventoryStatusCode.RELEASED },
                  ]}
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-600">Packing Type:</span>
                <Select
                  value={bulkPackingType}
                  onChange={setBulkPackingType}
                  placeholder="Select to update all"
                  style={{ width: 180 }}
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                  options={[
                    { label: "Loose", value: PackingTypes.LOOSE },
                    { label: "Slipcase", value: PackingTypes.SLIPCASE },
                    { label: "CB Fold", value: PackingTypes.CB_FOLD },
                    { label: "Shadow Box", value: PackingTypes.SHADOW_BOX },
                    { label: "Floating Shadow Box", value: PackingTypes.FLOATING_SHADOW_BOX },
                    { label: "Poly Only", value: PackingTypes.POLY_ONLY },
                    { label: "Travel Frame", value: PackingTypes.TRAVEL_FRAME },
                    { label: "Crate", value: PackingTypes.CRATE },
                    { label: "Slat Crate", value: PackingTypes.SLATCRATE },
                    { label: "Pallet", value: PackingTypes.PALLET },
                    { label: "Commercial Bin", value: PackingTypes.COMMERCIAL_BIN },
                    { label: "C-Bin", value: PackingTypes.CBIN },
                    { label: "Unknown Packaging", value: PackingTypes.UNKNOWN_PACKAGING },
                    { label: "Box", value: PackingTypes.BOX },
                    { label: "Custom", value: PackingTypes.CUSTOM },
                    { label: "T-Frame", value: PackingTypes.T_Frame },
                    { label: "Blanket Wrapped", value: PackingTypes.BLANKET_WRAPPED },
                    { label: "Poly/Foam", value: PackingTypes.POLY_FOAM },
                    { label: "Poly/Foam/CB", value: PackingTypes.POLY_FOAM_CB },
                  ]}
                />
              </div>
              <Button
                type="primary"
                size="small"
                onClick={handleBulkUpdate}
                disabled={!bulkItemType && !bulkStatus && !bulkPackingType}
              >
                Apply to All
              </Button>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Tag color="blue">{editedData.length} item{editedData.length !== 1 ? "s" : ""}</Tag>
              <Button
                size="small"
                icon={<PlusOutlined />}
                onClick={handleAddRow}
              >
                Add Row
              </Button>
              {lastDeletedRow && (
                <Button
                  size="small"
                  onClick={handleUndoDelete}
                >
                  Undo Delete
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">
                Reconfirm Client <span className="text-red-500">*</span>
              </span>
              <Select
                placeholder="Select Client"
                value={selectedClientId}
                onChange={setSelectedClientId}
                style={{ width: 300 }}
                showSearch
                status={!selectedClientId ? "error" : undefined}
                filterOption={(input, option) =>
                  (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
                options={clients.map((client) => ({
                  value: client.id,
                  label: client.companyName || `${client.firstName || ""} ${client.lastName || ""}`.trim() || "Unnamed Client",
                }))}
                loading={clientsLoading}
              />
            </div>
          </div>

          <div style={{ maxHeight: inline ? undefined : "600px", overflow: "auto", overscrollBehaviorX: "none" }}>
            <Table
              columns={columns}
              dataSource={editedData}
              rowKey={(record, index) => `row-${index}`}
              pagination={{ pageSize: 20 }}
              size="small"
              scroll={{ x: 1400 }}
            />
          </div>

          {inline && (
            <div className="flex justify-end gap-3 mt-4 pt-4 border-t">
              <Button onClick={handleClose}>Cancel</Button>
              <Button
                type="primary"
                onClick={handleImport}
                loading={importMutation.isPending}
                disabled={!selectedClientId || editedData.length === 0}
              >
                Import {editedData.length} Item{editedData.length !== 1 ? "s" : ""}
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );

  if (inline) {
    if (!open) return null;
    return (
      <div className="mt-6 bg-white border rounded-lg p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Review & Import CSV Inventory</h2>
          <Button onClick={handleClose} type="text" icon={<CloseOutlined />} />
        </div>
        {content}
      </div>
    );
  }

  return (
    <Modal
      open={open}
      title="Review & Import CSV Inventory"
      onCancel={handleClose}
      footer={[
        <Button key="cancel" onClick={handleClose}>
          Cancel
        </Button>,
        currentStep === 1 && (
          <Button
            key="import"
            type="primary"
            onClick={handleImport}
            loading={importMutation.isPending}
            disabled={!selectedClientId || editedData.length === 0}
          >
            Import {editedData.length} Item{editedData.length !== 1 ? "s" : ""}
          </Button>
        ),
      ]}
      width={1400}
    >
      {content}
    </Modal>
  );
};

